import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Printer, Undo2, X as XIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useUserNames } from "@/lib/useUserNames";

function exportCsv(filename, rows, columns) {
  const lines = [columns.map(c => c.label).join(",")];
  for (const r of rows) lines.push(columns.map(c => JSON.stringify(r[c.key] ?? "")).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/** Print a report as a styled PDF (via the print dialog). */
function printReport({ title, subtitle, columns, rows, summary, from, to, hospital }) {
  const w = window.open("", "_blank");
  if (!w) return;
  const esc = (v) => String(v ?? "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
  const headers = columns.map(c => `<th style="text-align:${c.align || 'left'};padding:6px 8px;background:#0F4C81;color:#fff;font-size:10px;letter-spacing:1px;text-transform:uppercase;font-weight:600;border:1px solid #E5E7EB;">${esc(c.label)}</th>`).join("");
  const body = rows.length === 0
    ? `<tr><td colspan="${columns.length}" style="padding:24px;text-align:center;color:#6B7280;font-style:italic;">No data in selected date range.</td></tr>`
    : rows.map((r, i) => `<tr style="background:${i % 2 === 0 ? '#FFFFFF' : '#F8FAFC'};">` +
        columns.map(c => `<td style="padding:6px 8px;font-size:11px;border:1px solid #E5E7EB;text-align:${c.align || 'left'};${c.mono ? 'font-family:monospace;' : ''}">${esc(c.render ? c.render(r) : (r[c.key] ?? "—"))}</td>`).join("") +
        "</tr>").join("");
  const summaryRow = summary && summary.length
    ? `<div style="margin:14px 0 8px;padding:10px 14px;background:#F8FAFC;border:1px solid #E5E7EB;border-radius:6px;display:flex;flex-wrap:wrap;gap:18px;">${summary.map(s => `<div style="font-size:12px;"><span style="color:#6B7280;text-transform:uppercase;letter-spacing:1px;font-size:10px;">${esc(s.label)}</span><div style="font-weight:600;font-size:14px;color:#0F4C81;">${esc(s.value)}</div></div>`).join("")}</div>`
    : "";
  w.document.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title>
    <style>
      @page { size: A4 landscape; margin: 14mm 12mm; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1A1A1A; margin: 0; padding: 16px; }
      h1 { font-size: 22px; margin: 0; color: #0F4C81; }
      .meta { color: #6B7280; font-size: 11px; margin-top: 4px; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      .footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #E5E7EB; color: #6B7280; font-size: 10px; display: flex; justify-content: space-between; }
    </style></head><body>
    <h1>${esc(title)}</h1>
    <div class="meta">${esc(subtitle || '')}${hospital ? ` · ${esc(hospital)}` : ''}${from ? ` · From: <b>${esc(from)}</b> to <b>${esc(to)}</b>` : ''}</div>
    ${summaryRow}
    <table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table>
    <div class="footer"><span>Generated on ${new Date().toLocaleString('en-IN')}</span><span>CareO · EMR for Eye Care</span></div>
    <script>window.onload = () => { setTimeout(() => window.print(), 200); };</script>
    </body></html>`);
  w.document.close();
}

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const dt  = (s) => (s || "").slice(0, 16).replace("T", " ");

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400e3).toISOString().slice(0, 10);
  const nameOf = useUserNames();
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [opd, setOpd] = useState({ items: [], total_visits: 0, total_billed: 0, total_collected: 0 });
  const [coll, setColl] = useState({ items: [], total_collected: 0, total_count: 0, by_user: [], by_mode: [] });
  const [ot, setOt] = useState({ items: [], totals: {} });
  const [surgeons, setSurgeons] = useState({ items: [], grand_total: 0 });
  const [ipd, setIpd] = useState({ items: [], total_admissions: 0, total_billed: 0, total_collected: 0 });
  const [bills, setBills] = useState({ items: [], total_bills: 0, total_billed: 0, total_collected: 0, total_refunded: 0 });
  const [receipts, setReceipts] = useState({ items: [], by_user: [], total_count: 0, total_amount: 0, total_cancelled: 0 });
  const [cancels, setCancels] = useState({ items: [], total_count: 0, admissions_cancelled: 0, receipts_cancelled: 0, total_receipt_amount: 0 });

  // Receipt-row action dialog (Cancel advance / Refund any received receipt).
  // `action.kind` is "cancel" | "refund"; the row payload lives in `action.row`.
  const [action, setAction] = useState(null);          // { kind, row } | null
  const [reason, setReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMode, setRefundMode] = useState("cash");
  const [busy, setBusy] = useState(false);

  const openCancel = (row) => { setAction({ kind: "cancel", row }); setReason(""); };
  const openRefund = (row) => {
    setAction({ kind: "refund", row });
    setReason("");
    setRefundAmount(String(Math.abs(row.amount || 0)));
    setRefundMode(row.mode || "cash");
  };
  const closeAction = () => { setAction(null); setReason(""); setRefundAmount(""); };

  const submitAction = async () => {
    if (!action) return;
    const { kind, row } = action;
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      if (kind === "cancel") {
        await api.post(`/billing/invoices/${row.invoice_id}/advance/${row.advance_idx}/cancel`, { reason: reason.trim() });
        toast.success(`Receipt ${row.receipt_no || ""} cancelled`);
      } else {
        const amt = parseFloat(refundAmount);
        if (!(amt > 0)) { toast.error("Refund amount must be > 0"); setBusy(false); return; }
        const { data } = await api.post(`/billing/invoices/${row.invoice_id}/refund`, {
          amount: amt, reason: reason.trim(), mode: refundMode, reference: "",
        });
        toast.success(`Refund ${data.refund_no} issued · ₹${amt.toFixed(2)}`);
      }
      closeAction();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const load = async () => {
    const params = { from_date: from, to_date: to };
    const [o, c, otr, sd, ip, bl, rc, cn] = await Promise.all([
      api.get("/reports/opd-register",       { params }),
      api.get("/reports/collections",        { params }),
      api.get("/reports/ot-register",        { params }),
      api.get("/reports/surgery-by-doctor",  { params }),
      api.get("/reports/ipd-register",       { params }),
      api.get("/reports/bill-register",      { params }),
      api.get("/reports/receipt-register",   { params }),
      api.get("/reports/cancel-register",    { params }),
    ]);
    setOpd(o.data); setColl(c.data); setOt(otr.data); setSurgeons(sd.data);
    setIpd(ip.data); setBills(bl.data); setReceipts(rc.data); setCancels(cn.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      <div>
        <div className="section-label">Analytics</div>
        <h1 className="font-heading text-3xl font-semibold">Reports &amp; Registers</h1>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div><Label>From</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="reports-from" /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="reports-to" /></div>
          <div className="md:col-span-2 flex items-end"><Button onClick={load} className="bg-brand hover:bg-brand-700" data-testid="reports-apply">Apply</Button></div>
        </div>
      </Card>

      <Tabs defaultValue="opd-register">
        <TabsList className="flex-wrap">
          <TabsTrigger value="opd-register"     data-testid="reports-tab-opd-register">OPD register</TabsTrigger>
          <TabsTrigger value="ipd-register"     data-testid="reports-tab-ipd-register">IPD register</TabsTrigger>
          <TabsTrigger value="bill-register"    data-testid="reports-tab-bill-register">Bill register</TabsTrigger>
          <TabsTrigger value="receipt-register" data-testid="reports-tab-receipt-register">Receipt register</TabsTrigger>
          <TabsTrigger value="cancel-register"  data-testid="reports-tab-cancel-register">Cancel register</TabsTrigger>
          <TabsTrigger value="ot"               data-testid="reports-tab-ot">OT register</TabsTrigger>
          <TabsTrigger value="surgeon"          data-testid="reports-tab-surgeon">Surgery by doctor</TabsTrigger>
          <TabsTrigger value="collections"      data-testid="reports-tab-collections">Collections</TabsTrigger>
        </TabsList>

        {/* ===== OPD Register ===== */}
        <TabsContent value="opd-register" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">OPD Register</div>
                <div className="mt-1 flex gap-3 text-[11px] text-ink-muted">
                  <span>Visits: <b>{opd.total_visits}</b></span>
                  <span>Billed: <b className="text-ink">{inr(opd.total_billed)}</b></span>
                  <span>Collected: <b className="text-leaf">{inr(opd.total_collected)}</b></span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "OPD Register", subtitle: `${opd.total_visits} visits`, from, to,
                  summary: [
                    { label: "Visits", value: String(opd.total_visits) },
                    { label: "Billed", value: inr(opd.total_billed) },
                    { label: "Collected", value: inr(opd.total_collected) },
                  ],
                  columns: [
                    { key: "visit_date", label: "Date", render: r => (r.visit_date || "").slice(0,16).replace("T", " ") },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "patient_mobile", label: "Mobile", mono: true },
                    { key: "doctor_name", label: "Doctor" },
                    { key: "invoice_no", label: "Invoice", mono: true },
                    { key: "billed", label: "Billed", align: "right", render: r => inr(r.billed) },
                    { key: "collected", label: "Collected", align: "right", render: r => inr(r.collected) },
                    { key: "balance", label: "Balance", align: "right", render: r => inr(r.balance) },
                    { key: "status", label: "Status" },
                  ], rows: opd.items,
                })} data-testid="print-opd-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("opd-register.csv", opd.items, [
                  { key: "visit_date", label: "Date" }, { key: "type", label: "Type" }, { key: "patient_uhid", label: "UHID" },
                  { key: "patient_name", label: "Patient" }, { key: "patient_mobile", label: "Mobile" },
                  { key: "doctor_name", label: "Doctor" }, { key: "status", label: "Status" },
                  { key: "invoice_no", label: "Invoice" }, { key: "billed", label: "Billed" },
                  { key: "collected", label: "Collected" }, { key: "balance", label: "Balance" },
                ])} data-testid="export-opd-register"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Mobile</th>
                    <th className="px-3 py-2 text-left">Doctor</th>
                    <th className="px-3 py-2 text-left">Invoice</th>
                    <th className="px-3 py-2 text-right">Billed</th>
                    <th className="px-3 py-2 text-right">Collected</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {opd.items.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-muted">No OPD visits in range.</td></tr>}
                  {opd.items.map(v => (
                    <tr key={v.visit_id}>
                      <td className="px-3 py-2 text-xs">{v.visit_date}</td>
                      <td className="px-3 py-2"><div className="font-medium">{v.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{v.patient_uhid}</div></td>
                      <td className="px-3 py-2 text-xs">{v.patient_mobile || "—"}</td>
                      <td className="px-3 py-2 text-xs">{v.doctor_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{v.invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-right">{inr(v.billed)}</td>
                      <td className="px-3 py-2 text-right text-leaf">{inr(v.collected)}</td>
                      <td className={`px-3 py-2 text-right ${v.balance > 0 ? "text-rose-700" : "text-ink-muted"}`}>{inr(v.balance)}</td>
                      <td className="px-3 py-2 text-xs capitalize">{(v.status || "").replace("_", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== IPD Register ===== */}
        <TabsContent value="ipd-register" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">IPD Register</div>
                <div className="mt-1 flex gap-3 text-[11px] text-ink-muted">
                  <span>Admissions: <b>{ipd.total_admissions}</b></span>
                  <span>Billed: <b>{inr(ipd.total_billed)}</b></span>
                  <span>Collected: <b className="text-leaf">{inr(ipd.total_collected)}</b></span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "IPD Register", from, to,
                  summary: [
                    { label: "Admissions", value: String(ipd.total_admissions) },
                    { label: "Billed", value: inr(ipd.total_billed) },
                    { label: "Collected", value: inr(ipd.total_collected) },
                  ],
                  columns: [
                    { key: "ipd_no", label: "IPD No.", mono: true },
                    { key: "admitted_at", label: "Admitted", render: r => (r.admitted_at || "").slice(0,16).replace("T", " ") },
                    { key: "discharged_at", label: "Discharged", render: r => (r.discharged_at || "").slice(0,16).replace("T", " ") || "—" },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "admission_type", label: "Type" },
                    { key: "ward", label: "Ward" },
                    { key: "bed_no", label: "Bed" },
                    { key: "status", label: "Status" },
                    { key: "payer_category", label: "Payer" },
                    { key: "panel_name", label: "Panel" },
                    { key: "invoice_no", label: "Invoice", mono: true },
                    { key: "billed", label: "Billed", align: "right", render: r => inr(r.billed) },
                    { key: "collected", label: "Collected", align: "right", render: r => inr(r.collected) },
                    { key: "balance", label: "Balance", align: "right", render: r => inr(r.balance) },
                  ], rows: ipd.items,
                })} data-testid="print-ipd-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("ipd-register.csv", ipd.items, [
                  { key: "ipd_no", label: "IPD No." }, { key: "admitted_at", label: "Admitted" }, { key: "discharged_at", label: "Discharged" },
                  { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" }, { key: "admission_type", label: "Type" },
                  { key: "ward", label: "Ward" }, { key: "bed_no", label: "Bed" }, { key: "status", label: "Status" },
                  { key: "payer_category", label: "Payer" }, { key: "panel_name", label: "Panel" }, { key: "policy_no", label: "Policy" },
                  { key: "invoice_no", label: "Invoice" }, { key: "billed", label: "Billed" },
                  { key: "collected", label: "Collected" }, { key: "balance", label: "Balance" },
                ])} data-testid="export-ipd-register"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">IPD No.</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Admitted</th>
                    <th className="px-3 py-2 text-left">Discharged</th>
                    <th className="px-3 py-2 text-left">Ward / Bed</th>
                    <th className="px-3 py-2 text-left">Payer</th>
                    <th className="px-3 py-2 text-left">Invoice</th>
                    <th className="px-3 py-2 text-right">Billed</th>
                    <th className="px-3 py-2 text-right">Collected</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {ipd.items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted">No admissions in range.</td></tr>}
                  {ipd.items.map(a => (
                    <tr key={a.admission_id} className={a.status === "cancelled" ? "bg-rose-50/40" : ""}>
                      <td className="px-3 py-2 font-mono text-xs">{a.ipd_no || "—"}</td>
                      <td className="px-3 py-2"><div className="font-medium">{a.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{a.patient_uhid}</div></td>
                      <td className="px-3 py-2 text-xs">{dt(a.admitted_at)}</td>
                      <td className="px-3 py-2 text-xs">{a.discharged_at ? dt(a.discharged_at) : "—"}</td>
                      <td className="px-3 py-2 text-xs">{(a.ward || "—") + (a.bed_no ? ` · ${a.bed_no}` : "")}</td>
                      <td className="px-3 py-2 text-xs"><div className="capitalize">{a.payer_category}</div>{a.panel_name && <div className="text-[10px] text-ink-muted">{a.panel_name}{a.policy_no ? ` · ${a.policy_no}` : ""}</div>}</td>
                      <td className="px-3 py-2 font-mono text-xs">{a.invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-right">{inr(a.billed)}</td>
                      <td className="px-3 py-2 text-right text-leaf">{inr(a.collected)}</td>
                      <td className="px-3 py-2 text-xs"><Badge variant="outline" className={a.status === "cancelled" ? "border-rose-300 bg-rose-50 text-rose-700" : a.status === "discharged" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}>{a.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Bill Register ===== */}
        <TabsContent value="bill-register" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">Bill Register</div>
                <div className="mt-1 flex gap-3 text-[11px] text-ink-muted">
                  <span>Bills: <b>{bills.total_bills}</b></span>
                  <span>Billed: <b>{inr(bills.total_billed)}</b></span>
                  <span>Collected: <b className="text-leaf">{inr(bills.total_collected)}</b></span>
                  <span>Refunded: <b className="text-rose-700">{inr(bills.total_refunded)}</b></span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "Bill Register", from, to,
                  summary: [
                    { label: "Bills", value: String(bills.total_bills) },
                    { label: "Billed", value: inr(bills.total_billed) },
                    { label: "Collected", value: inr(bills.total_collected) },
                    { label: "Refunded", value: inr(bills.total_refunded) },
                  ],
                  columns: [
                    { key: "invoice_no", label: "Invoice", mono: true },
                    { key: "created_at", label: "Date", render: r => (r.created_at || "").slice(0,16).replace("T", " ") },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "bill_type", label: "Type" },
                    { key: "status", label: "Status" },
                    { key: "panel_name", label: "Panel" },
                    { key: "payment_mode", label: "Mode" },
                    { key: "billed", label: "Billed", align: "right", render: r => inr(r.billed) },
                    { key: "collected", label: "Collected", align: "right", render: r => inr(r.collected) },
                    { key: "balance", label: "Balance", align: "right", render: r => inr(r.balance) },
                    { key: "refunded", label: "Refunded", align: "right", render: r => inr(r.refunded) },
                  ], rows: bills.items,
                })} data-testid="print-bill-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("bill-register.csv", bills.items, [
                  { key: "invoice_no", label: "Invoice" }, { key: "created_at", label: "Date" },
                  { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" },
                  { key: "bill_type", label: "Type" }, { key: "bill_state", label: "State" }, { key: "status", label: "Status" },
                  { key: "panel_name", label: "Panel" }, { key: "payment_mode", label: "Mode" },
                  { key: "billed", label: "Billed" }, { key: "collected", label: "Collected" },
                  { key: "balance", label: "Balance" }, { key: "refunded", label: "Refunded" },
                  { key: "created_by_email", label: "Created by" },
                ])} data-testid="export-bill-register"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Invoice</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">State</th>
                    <th className="px-3 py-2 text-left">Mode / Panel</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-left">Created by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {bills.items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted">No bills in range.</td></tr>}
                  {bills.items.map(b => (
                    <tr key={b.invoice_id} className={b.status === "cancelled" ? "bg-rose-50/40 line-through" : ""}>
                      <td className="px-3 py-2 font-mono text-xs"><a href={`/print/invoice/${b.invoice_id}`} target="_blank" rel="noreferrer" className="text-brand hover:underline">{b.invoice_no}</a></td>
                      <td className="px-3 py-2 text-xs">{dt(b.created_at)}</td>
                      <td className="px-3 py-2"><div className="font-medium">{b.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{b.patient_uhid}</div></td>
                      <td className="px-3 py-2 text-xs capitalize">{b.bill_type}</td>
                      <td className="px-3 py-2 text-xs"><Badge variant="outline" className={b.bill_state === "final" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}>{b.bill_state}</Badge></td>
                      <td className="px-3 py-2 text-xs"><div className="capitalize">{(b.payment_mode || "").replace("_"," ")}</div>{b.panel_name && <div className="text-[10px] text-ink-muted">{b.panel_name}</div>}</td>
                      <td className="px-3 py-2 text-right">{inr(b.billed)}</td>
                      <td className="px-3 py-2 text-right text-leaf">{inr(b.collected)}</td>
                      <td className={`px-3 py-2 text-right ${b.balance > 0 ? "text-rose-700" : ""}`}>{inr(b.balance)}</td>
                      <td className="px-3 py-2 text-xs">{nameOf(b.created_by_email, "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Receipt Register ===== */}
        <TabsContent value="receipt-register" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">Receipt Register</div>
                <div className="mt-1 flex gap-3 text-[11px] text-ink-muted">
                  <span>Active receipts: <b>{receipts.total_count}</b></span>
                  <span>Collected: <b className="text-leaf">{inr(receipts.total_amount)}</b></span>
                  <span>Cancelled: <b className="text-rose-700">{receipts.total_cancelled}</b></span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "Receipt Register", from, to,
                  summary: [
                    { label: "Active receipts", value: String(receipts.total_count) },
                    { label: "Collected", value: inr(receipts.total_amount) },
                    { label: "Cancelled", value: String(receipts.total_cancelled) },
                  ],
                  columns: [
                    { key: "received_at", label: "Date", render: r => (r.received_at || "").slice(0,16).replace("T", " ") },
                    { key: "invoice_no", label: "Invoice", mono: true },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "amount", label: "Amount", align: "right", render: r => inr(r.amount) },
                    { key: "mode", label: "Mode" },
                    { key: "reference", label: "Reference", mono: true },
                    { key: "received_by", label: "Received by" },
                    { key: "cancelled", label: "Cancelled", render: r => r.cancelled ? "Yes" : "—" },
                  ], rows: receipts.items,
                })} data-testid="print-receipt-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("receipt-register.csv", receipts.items, [
                  { key: "received_at", label: "Date" }, { key: "invoice_no", label: "Invoice" },
                  { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" },
                  { key: "amount", label: "Amount" }, { key: "mode", label: "Mode" }, { key: "reference", label: "Reference" },
                  { key: "received_by", label: "Received by" }, { key: "cancelled", label: "Cancelled" },
                  { key: "cancel_reason", label: "Cancel reason" },
                ])} data-testid="export-receipt-register"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>

            {/* By-user summary */}
            {receipts.by_user.length > 0 && (
              <div className="border-b border-border bg-secondary/30 p-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">User-wise collection</div>
                <div className="flex flex-wrap gap-2">
                  {receipts.by_user.map(u => (
                    <div key={u.received_by} className="rounded-md border border-border bg-white px-3 py-1.5 text-xs">
                      <div className="font-medium">{nameOf(u.received_by, u.received_by)}</div>
                      <div className="text-[10px] text-ink-muted">{u.count} receipts · <span className="text-leaf font-semibold">{inr(u.amount)}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Received at</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Receipt</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Mode</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Received by</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {receipts.items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted">No receipts in range.</td></tr>}
                  {receipts.items.map((r, i) => {
                    const isRefund = r.kind === "refund";
                    const isSettle = r.kind === "settlement";
                    const isOpdBill = r.kind === "opd_bill";
                    // Build the correct print link based on type
                    let printHref = "";
                    if (r.kind === "advance" && r.advance_idx !== undefined) {
                      printHref = `/print/receipt/${r.invoice_id}/${r.advance_idx}`;
                    } else if (isRefund && r.receipt_no) {
                      printHref = `/print/refund-receipt/${r.invoice_id}/${encodeURIComponent(r.receipt_no)}`;
                    } else if (isOpdBill || isSettle) {
                      printHref = `/print/invoice/${r.invoice_id}`;
                    }
                    const typeBadge = {
                      advance: { label: "Advance", cls: "border-amber-300 bg-amber-50 text-amber-800" },
                      settlement: { label: "Settlement", cls: "border-sky-300 bg-sky-50 text-sky-800" },
                      refund: { label: "Refund", cls: "border-rose-300 bg-rose-50 text-rose-800" },
                      opd_bill: { label: "OPD Bill", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
                    }[r.kind] || { label: r.kind || "—", cls: "" };
                    return (
                      <tr key={`${r.invoice_id}-${r.kind}-${r.advance_idx ?? r.receipt_no ?? i}`} className={r.cancelled ? "bg-rose-50/40" : ""}>
                        <td className="px-3 py-2 text-xs">{dt(r.received_at)}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className={typeBadge.cls}>{typeBadge.label}</Badge></td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {printHref ? (
                            <a href={printHref} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                              {r.receipt_no || r.invoice_no}
                            </a>
                          ) : (
                            <span>{r.receipt_no || r.invoice_no}</span>
                          )}
                          {r.invoice_no && r.receipt_no && r.receipt_no !== r.invoice_no && (
                            <div className="text-[10px] text-ink-muted">on {r.invoice_no}</div>
                          )}
                        </td>
                        <td className="px-3 py-2"><div className={r.cancelled ? "line-through" : "font-medium"}>{r.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{r.patient_uhid}</div></td>
                        <td className={`px-3 py-2 text-right ${r.cancelled ? "line-through text-ink-muted" : isRefund ? "text-rose-700 font-semibold" : "text-leaf font-semibold"}`}>{inr(r.amount)}</td>
                        <td className="px-3 py-2 text-xs capitalize">{(r.mode || "").replace("_", " ")}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.reference || "—"}</td>
                        <td className="px-3 py-2 text-xs">{r.received_by_name || nameOf(r.received_by, "—")}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.cancelled
                            ? <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">Cancelled</Badge>
                            : <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Active</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {/* Cancel only for non-cancelled advances (bill must be running — backend enforces). */}
                            {r.kind === "advance" && !r.cancelled && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-700 hover:bg-rose-50"
                                onClick={() => openCancel(r)} data-testid={`receipt-cancel-${i}`}>
                                <XIcon size={12} className="mr-1" />Cancel
                              </Button>
                            )}
                            {/* Refund for any non-cancelled, money-in receipt (not for refund rows or already-cancelled rows). */}
                            {!r.cancelled && r.kind !== "refund" && r.amount > 0 && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-800 hover:bg-amber-50"
                                onClick={() => openRefund(r)} data-testid={`receipt-refund-${i}`}>
                                <Undo2 size={12} className="mr-1" />Refund
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Cancel Register ===== */}
        <TabsContent value="cancel-register" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">Cancellation Register</div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-muted">
                  <span>Total cancelled: <b>{cancels.total_count}</b></span>
                  <span>Admissions: <b>{cancels.admissions_cancelled}</b></span>
                  <span>Bills: <b>{cancels.bills_cancelled || 0}</b>{cancels.total_bill_amount ? ` (${inr(cancels.total_bill_amount)})` : ""}</span>
                  <span>Receipts: <b>{cancels.receipts_cancelled}</b> ({inr(cancels.total_receipt_amount)})</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "Cancellation Register", from, to,
                  summary: [
                    { label: "Total cancelled", value: String(cancels.total_count) },
                    { label: "Admissions", value: String(cancels.admissions_cancelled) },
                    { label: "Receipts", value: `${cancels.receipts_cancelled} (${inr(cancels.total_receipt_amount)})` },
                  ],
                  columns: [
                    { key: "cancelled_at", label: "Cancelled at", render: r => (r.cancelled_at || "").slice(0,16).replace("T", " ") },
                    { key: "kind", label: "Kind" },
                    { key: "ref", label: "Reference", mono: true },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "amount", label: "Amount", align: "right", render: r => inr(r.amount) },
                    { key: "cancelled_by", label: "Cancelled by" },
                    { key: "reason", label: "Reason" },
                  ], rows: cancels.items,
                })} data-testid="print-cancel-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("cancel-register.csv", cancels.items, [
                  { key: "kind", label: "Kind" }, { key: "ref", label: "Ref" },
                  { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" },
                  { key: "amount", label: "Amount" }, { key: "cancelled_at", label: "Cancelled at" },
                  { key: "cancelled_by", label: "Cancelled by" }, { key: "reason", label: "Reason" },
                ])} data-testid="export-cancel-register"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Cancelled at</th>
                    <th className="px-3 py-2 text-left">Kind</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Cancelled by</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {cancels.items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted">No cancellations in range.</td></tr>}
                  {cancels.items.map((c, i) => (
                    <tr key={`${c.kind}-${c.ref}-${i}`}>
                      <td className="px-3 py-2 text-xs">{dt(c.cancelled_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="outline" className={c.kind === "admission" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-rose-300 bg-rose-50 text-rose-700"}>
                          {c.kind === "admission" ? "IPD admission" : "Receipt"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{c.ref || "—"}</td>
                      <td className="px-3 py-2"><div>{c.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{c.patient_uhid}</div></td>
                      <td className="px-3 py-2 text-right">{c.amount > 0 ? inr(c.amount) : "—"}</td>
                      <td className="px-3 py-2 text-xs">{nameOf(c.cancelled_by, "—")}</td>
                      <td className="px-3 py-2 text-xs">{c.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== OT Register (kept from before) ===== */}
        <TabsContent value="ot" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">OT register</div>
                <div className="mt-1 flex gap-2 text-[11px] text-ink-muted">
                  {Object.entries(ot.totals || {}).map(([k, v]) => v > 0 && <span key={k} className="rounded-full border border-border bg-secondary px-2 py-0.5">{k.replace("_"," ")}: <b>{v}</b></span>)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "OT Register (Surgery)", from, to,
                  summary: Object.entries(ot.totals || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ label: k.replace("_", " "), value: String(v) })),
                  columns: [
                    { key: "scheduled_date", label: "Date", render: r => (r.scheduled_date || "").slice(0,16).replace("T", " ") },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "surgery_name", label: "Surgery" },
                    { key: "eye", label: "Eye" },
                    { key: "status", label: "Status" },
                    { key: "surgeon_name", label: "Surgeon" },
                    { key: "ot_room", label: "OT" },
                  ], rows: ot.items,
                })} data-testid="print-ot-register"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("ot-register.csv", ot.items, [
                  { key: "scheduled_date", label: "Date" }, { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" },
                  { key: "surgery_name", label: "Surgery" }, { key: "eye", label: "Eye" }, { key: "status", label: "Status" },
                ])} data-testid="export-ot"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Patient</th><th className="px-3 py-2 text-left">Surgery</th><th className="px-3 py-2 text-left">Eye</th><th className="px-3 py-2 text-left">Status</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {ot.items.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-ink-muted">No surgeries in range.</td></tr>}
                  {ot.items.map(o => (
                    <tr key={o.id}><td className="px-3 py-2">{o.scheduled_date}</td><td className="px-3 py-2"><div className="font-medium">{o.patient_name}</div><div className="text-[11px] font-mono text-ink-muted">{o.patient_uhid}</div></td><td className="px-3 py-2">{o.surgery_name}</td><td className="px-3 py-2">{o.eye}</td><td className="px-3 py-2 capitalize">{o.status.replace("_"," ")}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Surgery by Doctor ===== */}
        <TabsContent value="surgeon" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-border p-3">
              <div className="font-heading text-base font-semibold">Surgery by doctor <span className="ml-2 text-xs font-body text-ink-muted">(completed only)</span></div>
              <div className="flex items-center gap-3 text-sm">
                <div>Total completed: <b className="text-brand">{surgeons.grand_total}</b></div>
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "Surgery by Doctor", subtitle: "Completed only", from, to,
                  summary: [{ label: "Total completed", value: String(surgeons.grand_total) }],
                  columns: [
                    { key: "surgeon_name", label: "Surgeon" },
                    { key: "total", label: "Total", align: "right" },
                    { key: "od", label: "OD", align: "right", render: r => r.by_eye?.OD || 0 },
                    { key: "os", label: "OS", align: "right", render: r => r.by_eye?.OS || 0 },
                    { key: "ou", label: "OU", align: "right", render: r => r.by_eye?.OU || 0 },
                    { key: "procedures", label: "By procedure", render: r => Object.entries(r.by_procedure || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") },
                  ], rows: surgeons.items,
                })} data-testid="print-surgeons"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("surgery-by-doctor.csv",
                  surgeons.items.map(s => ({ surgeon_name: s.surgeon_name, total: s.total, OD: s.by_eye?.OD || 0, OS: s.by_eye?.OS || 0, OU: s.by_eye?.OU || 0, by_procedure: Object.entries(s.by_procedure || {}).map(([k, v]) => `${k}:${v}`).join(";") })),
                  [{ key: "surgeon_name", label: "Surgeon" }, { key: "total", label: "Total" }, { key: "OD", label: "OD" }, { key: "OS", label: "OS" }, { key: "OU", label: "OU" }, { key: "by_procedure", label: "By procedure" }]
                )} data-testid="export-surgeons"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2 text-left">Surgeon</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">OD</th><th className="px-3 py-2 text-right">OS</th><th className="px-3 py-2 text-right">OU</th><th className="px-3 py-2 text-left">By procedure</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {surgeons.items.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-ink-muted">No completed surgeries in range.</td></tr>}
                  {surgeons.items.map(s => (
                    <tr key={s.surgeon_id}>
                      <td className="px-3 py-2 font-medium">{s.surgeon_name}</td>
                      <td className="px-3 py-2 text-right font-medium">{s.total}</td>
                      <td className="px-3 py-2 text-right">{s.by_eye.OD || 0}</td>
                      <td className="px-3 py-2 text-right">{s.by_eye.OS || 0}</td>
                      <td className="px-3 py-2 text-right">{s.by_eye.OU || 0}</td>
                      <td className="px-3 py-2 text-[11px]">{Object.entries(s.by_procedure).map(([k, v]) => `${k}: ${v}`).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Collections — every money-in event with user, kind & mode ===== */}
        <TabsContent value="collections" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
              <div>
                <div className="font-heading text-base font-semibold">Collections</div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-muted">
                  <span>Total receipts: <b>{coll.total_count}</b></span>
                  <span>Total collected: <b className="text-leaf">{inr(coll.total_collected)}</b></span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => printReport({
                  title: "Collections", from, to,
                  summary: [
                    { label: "Total receipts", value: String(coll.total_count) },
                    { label: "Total collected", value: inr(coll.total_collected) },
                  ],
                  columns: [
                    { key: "received_at", label: "When", render: r => (r.received_at || "").slice(0,16).replace("T", " ") },
                    { key: "kind", label: "Type" },
                    { key: "receipt_no", label: "Receipt", mono: true },
                    { key: "patient_uhid", label: "UHID", mono: true },
                    { key: "patient_name", label: "Patient" },
                    { key: "amount", label: "Amount", align: "right", render: r => inr(r.amount) },
                    { key: "mode", label: "Mode" },
                    { key: "received_by_name", label: "Received by", render: r => r.received_by_name || nameOf(r.received_by, "—") },
                  ], rows: coll.items,
                })} data-testid="print-collections"><Printer size={14} className="mr-2" />Print PDF</Button>
                <Button size="sm" variant="outline" onClick={() => exportCsv("collections.csv",
                  coll.items.map(r => ({ ...r, received_by_name: r.received_by_name || nameOf(r.received_by, "") })),
                  [
                    { key: "received_at", label: "When" }, { key: "kind", label: "Type" }, { key: "receipt_no", label: "Receipt" },
                    { key: "patient_uhid", label: "UHID" }, { key: "patient_name", label: "Patient" },
                    { key: "amount", label: "Amount" }, { key: "mode", label: "Mode" }, { key: "received_by_name", label: "Received by" },
                  ])} data-testid="export-collections"><Download size={14} className="mr-2" />Export CSV</Button>
              </div>
            </div>

            {/* By-user and by-mode summary chips */}
            {(coll.by_user?.length > 0 || coll.by_mode?.length > 0) && (
              <div className="grid gap-3 border-b border-border bg-secondary/30 p-3 md:grid-cols-2">
                {coll.by_user?.length > 0 && (
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-brand">By user</div>
                    <div className="flex flex-wrap gap-2">
                      {coll.by_user.map(u => (
                        <div key={u.user} className="rounded-md border border-border bg-white px-3 py-1.5 text-xs">
                          <div className="font-medium">{nameOf(u.user, u.user)}</div>
                          <div className="text-[10px] text-ink-muted">{u.count} receipts · <span className="text-leaf font-semibold">{inr(u.amount)}</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {coll.by_mode?.length > 0 && (
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#7C3AED" }}>By mode</div>
                    <div className="flex flex-wrap gap-2">
                      {coll.by_mode.map(m => (
                        <div key={m.mode} className="rounded-md border border-border bg-white px-3 py-1.5 text-xs">
                          <div className="font-medium capitalize">{(m.mode || "cash").replace("_", " ")}</div>
                          <div className="text-[10px] text-ink-muted">{m.count} · <span className="text-leaf font-semibold">{inr(m.amount)}</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Receipt</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Mode</th>
                    <th className="px-3 py-2 text-left">Received by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {coll.items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted">No collections in range.</td></tr>}
                  {coll.items.map((r, i) => {
                    const typeBadge = {
                      advance: { label: "Advance", cls: "border-amber-300 bg-amber-50 text-amber-800" },
                      settlement: { label: "Settlement", cls: "border-sky-300 bg-sky-50 text-sky-800" },
                      bill: { label: "Bill", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
                    }[r.kind] || { label: r.kind || "—", cls: "" };
                    return (
                      <tr key={`${r.invoice_id}-${r.kind}-${i}`}>
                        <td className="px-3 py-2 text-xs">{dt(r.received_at)}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className={typeBadge.cls}>{typeBadge.label}</Badge></td>
                        <td className="px-3 py-2 font-mono text-xs">{r.receipt_no || r.invoice_no}</td>
                        <td className="px-3 py-2"><div className="font-medium">{r.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{r.patient_uhid}</div></td>
                        <td className="px-3 py-2 text-right text-leaf font-semibold">{inr(r.amount)}</td>
                        <td className="px-3 py-2 text-xs capitalize">{(r.mode || "cash").replace("_", " ")}</td>
                        <td className="px-3 py-2 text-xs">{r.received_by_name || nameOf(r.received_by, "—")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

      </Tabs>

      {/* === Inline Cancel / Refund action dialog === */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="max-w-md" data-testid="receipt-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {action?.kind === "cancel" ? "Cancel receipt" : "Refund receipt"}
            </DialogTitle>
          </DialogHeader>
          {action && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-border bg-secondary/40 p-2 text-xs">
                <div><span className="text-ink-muted">Receipt:</span> <b className="font-mono">{action.row.receipt_no || action.row.invoice_no}</b></div>
                <div><span className="text-ink-muted">Patient:</span> {action.row.patient_name} · <span className="font-mono">{action.row.patient_uhid}</span></div>
                <div><span className="text-ink-muted">Amount on receipt:</span> <b>{inr(Math.abs(action.row.amount || 0))}</b> · {(action.row.mode || "").replace("_", " ")}</div>
              </div>
              {action.kind === "refund" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Refund amount *</Label>
                    <Input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Refund mode</Label>
                    <Select value={refundMode} onValueChange={setRefundMode}>
                      <SelectTrigger data-testid="refund-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["cash", "upi", "card", "bank_transfer", "cheque"].map(m =>
                          <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">{action.kind === "cancel" ? "Cancellation reason *" : "Refund reason *"}</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="receipt-action-reason" placeholder={action.kind === "cancel" ? "e.g. wrong amount entered" : "e.g. patient overpaid, package not used"} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={closeAction} disabled={busy} data-testid="receipt-action-cancel-btn">Close</Button>
                <Button
                  className={action.kind === "cancel" ? "bg-rose-700 hover:bg-rose-800" : "bg-amber-700 hover:bg-amber-800"}
                  onClick={submitAction} disabled={busy} data-testid="receipt-action-confirm-btn">
                  {busy ? "Working…" : action.kind === "cancel" ? "Cancel receipt" : "Issue refund"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
